import { useEffect, useState } from "react";
import { Cloud, Droplets, Waves } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Sylhet coordinates (Naljuri Inn area)
const LAT = 24.8949;
const LON = 91.8687;

// Top-right floating widget — visible on mobile too in compact form
export default function LiveContextWidget() {
  const { t, lang } = useLang();
  const [w, setW] = useState(null);

  useEffect(() => {
    let aborted = false;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&current=temperature_2m,weather_code,relative_humidity_2m&timezone=auto`)
      .then((r) => r.json())
      .then((data) => { if (!aborted) setW(data?.current); })
      .catch(() => {});
    return () => { aborted = true; };
  }, []);

  const code = w?.weather_code;
  const condition = describeWeather(code);

  return (
    <div data-testid="live-context-widget"
         className="absolute z-20 backdrop-blur-xl bg-forest-900/45 border border-cream-100/25 rounded-md text-cream-100 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]
                    top-24 right-4 p-3 max-w-[200px]
                    md:top-28 md:right-12 md:p-5 md:max-w-[260px]">
      <div className="flex items-center gap-2">
        <span className="live-dot" />
        <span className="text-[9px] md:text-[10px] uppercase tracking-widest2 text-cream-100/85">{t.hero.live}</span>
      </div>
      <div className="mt-2 md:mt-3 flex items-center gap-2">
        <Waves className="w-4 h-4 md:w-5 md:h-5 text-gold-400" />
        <span className={`text-sm md:text-base font-display ${lang==='bn'?'font-bn':''}`}>{t.hero.river_clear}</span>
      </div>
      <div className="my-2 md:my-3 hairline" />
      <div className="flex items-center justify-between text-cream-100/80 gap-3">
        <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
          <Cloud className="w-3.5 h-3.5 md:w-4 md:h-4 shrink-0" />
          <span className="text-[9px] md:text-[11px] uppercase tracking-widest2 truncate">{t.weather.now}</span>
        </div>
        {w ? (
          <div className="text-right">
            <div className="text-lg md:text-2xl font-display leading-none text-cream-100">{Math.round(w.temperature_2m)}°</div>
            <div className="text-[9px] md:text-[10px] mt-0.5 md:mt-1 text-cream-100/70 whitespace-nowrap">{condition}</div>
          </div>
        ) : (
          <div className="text-[9px] md:text-[10px] text-cream-100/60">…</div>
        )}
      </div>
      {w && (
        <div className="hidden md:flex mt-3 items-center gap-1.5 text-[10px] text-cream-100/65">
          <Droplets className="w-3 h-3" /> {Math.round(w.relative_humidity_2m)}% humidity
        </div>
      )}
    </div>
  );
}

function describeWeather(code) {
  if (code == null) return "—";
  if (code === 0) return "Clear";
  if ([1,2].includes(code)) return "Mostly clear";
  if (code === 3) return "Overcast";
  if ([45,48].includes(code)) return "Fog";
  if ([51,53,55,56,57].includes(code)) return "Drizzle";
  if ([61,63,65,66,67,80,81,82].includes(code)) return "Rain";
  if ([71,73,75,77,85,86].includes(code)) return "Snow";
  if ([95,96,99].includes(code)) return "Thunder";
  return "Calm";
}
